import React, { useState } from 'react';
import CheckIcon from '../../../assets/Images/check icon.svg';
import './SecondPricingSection.scss';
import UnifiedButton from '../../shared/Button';
import { Box } from '@mui/material';

const SIGN_UP_URL = 'https://app.fmdigitalofficial.com/sign-up';
const SALES_EMAIL = 'fmdigitalofficial@gmail.com';
const API_BASE = process.env.REACT_APP_API_BASE || '';
const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

// Plan columns, left to right. `id` is what /pricing/:id highlights.
// `action: "pay"` plans open the Razorpay checkout (see payForPlan) and
// redirect to sign-up on success; `action: "mail"` plans open the visitor's
// mail client instead.
const plans = [
    { id: 1, name: "Solo Creator", price: "Free account for special request", cta: "Request Now", action: "mail" },
    { id: 2, name: "Starter", price: "₹899 Per Release", cta: "Get Started", action: "pay", planKey: "pricing_starter" },
    {
        id: 3,
        name: "Career Artist",
        price: "Starting from ₹2999 onwards",
        priceDetail: "3 Labels | 10 Artists | 3 OAC | 95%",
        cta: "Get Started",
        action: "pay",
        planKey: "pricing_career_artist",
    },
    {
        id: 4,
        name: "Label Executive",
        price: "Starting from ₹12999 onwards",
        priceDetail: "15 Labels | 30 Artists | 15 OAC | 95%",
        cta: "Kickstart Your Company",
        action: "mail",
    },
];

// `values` is positional and must stay the same length/order as `plans`.
const pricingData = [
    {
        category: "Distribution",
        features: [
            { name: "Artist Profiles", values: ["20", "3", "10", "30"] },
            { name: "Label", values: ["1", "1", "3", "15"] },
            { name: "OAC", values: ["", "", "3", "15"] },
            {
                name: "Unlimited Distribution",
                subtext: "to 190+ DSPs",
                values: ["7 Business Days", "2 Business Days", "2 to 5 Business Days", "2 to 5 Business Days"],
            },
            { name: "Use Your UPC & ISRC", values: [true, true, true, true] },
            { name: "Track Credits", values: [true, true, true, true] },
            { name: "Release Edits", values: [false, true, true, true] },
            { name: "Pre-Save Links", values: [false, false, true, true] },
        ]
    },
    {
        category: "Royalties",
        features: [
            { name: "Royalties", values: ["70%", "70%", "95%", "95%"] },
            { name: "Financial Statements", values: ["Quarterly", "Quarterly", "Every Month", "Every Month"] },
            { name: "Splits", subtext: "Coming Soon", values: [false, false, true, true] },
            { name: "Royalty Advances", subtext: "Coming Soon", values: [false, false, true, true] },
        ]
    },
    {
        category: "Marketing & Label Services",
        features: [
            { name: "Playlist Pitching", values: [false, false, true, true] },
            { name: "Account Manager", values: [false, false, false, true] },
        ]
    }
];

// An excluded feature renders an explicit dash rather than an empty cell —
// a blank right-hand side reads as "we forgot to fill this in", especially
// on the mobile cards where there is no column to compare it against.
const renderValue = (value) => {
    if (value === true) return <img src={CheckIcon} alt="Included" />;
    if (value === false || value === '' || value == null) {
        return <span className="not-included" aria-label="Not included">—</span>;
    }
    return value;
};

function loadRazorpay() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const script = document.createElement('script');
        script.src = RAZORPAY_SRC;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

function SecondPricingSection({ selectedPlanId }) {
    const [payingPlanId, setPayingPlanId] = useState(null);

    const mailSales = () => {
        window.location.href = `mailto:${SALES_EMAIL}`;
    };

    const payForPlan = async (plan) => {
        setPayingPlanId(plan.id);
        try {
            const ok = await loadRazorpay();
            if (!ok) throw new Error('Could not load the payment gateway. Please check your connection.');

            const res = await fetch(`${API_BASE}/api/create-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ plan: plan.planKey }),
            });
            const data = await res.json();
            if (!data.order_id) throw new Error(data.error || 'Order creation failed');

            const rzp = new window.Razorpay({
                key: data.key_id,
                amount: data.amount,
                currency: data.currency,
                name: 'FMDO – FM Digital Official',
                description: plan.name,
                order_id: data.order_id,
                handler: async (response) => {
                    const vRes = await fetch(`${API_BASE}/api/verify-payment`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        }),
                    });
                    const vData = await vRes.json();
                    setPayingPlanId(null);
                    if (vData.verified) {
                        window.location.href = SIGN_UP_URL;
                    } else {
                        window.alert(`Payment received but verification failed. Please contact support with Payment ID: ${response.razorpay_payment_id}`);
                    }
                },
                modal: { ondismiss: () => setPayingPlanId(null) },
            });
            rzp.on('payment.failed', (response) => {
                window.alert(`Payment failed: ${response.error.description}. Please try again.`);
                setPayingPlanId(null);
            });
            rzp.open();
        } catch (err) {
            console.error(err);
            window.alert(err.message || 'Could not initiate payment. Please try again or contact support.');
            setPayingPlanId(null);
        }
    };

    const handlePlanCta = (plan) => () => {
        if (plan.action === 'pay') return payForPlan(plan);
        mailSales();
    };

    return (
        <div className="pricing-section">
            {/* Scroll container so the comparison table can never push the
                page sideways on tablets / small laptops. */}
            <div className="pricing-table-wrap u-scroll-x">
            <table className="pricing-table">
                <thead>
                    <tr>
                        <th></th>
                        {/* Apply selected class if the plan matches selectedPlanId */}
                        {plans.map((plan) => (
                            <th key={plan.id} className={selectedPlanId === plan.id ? 'selected' : ''}>
                                {plan.name}
                            </th>
                        ))}
                    </tr>
                    <tr className="plan-price-row">
                        <th></th>
                        {plans.map((plan) => (
                            <th key={plan.id} className={selectedPlanId === plan.id ? 'selected' : ''}>
                                {plan.price}
                                {plan.priceDetail && (
                                    <div className="price-detail">{plan.priceDetail}</div>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {pricingData.map((section, sectionIndex) => (
                        <React.Fragment key={`section-${sectionIndex}`}>
                            <tr className="section-title">
                                <td style={{ borderBottom: '1px solid black' }} colSpan={plans.length + 1}>{section.category}</td>
                            </tr>
                            {section.features.map((feature, index) => (
                                <tr key={`feature-${index}`} className={index % 2 === 1 ? 'highlighted-row' : ''}>
                                    <td>
                                        {feature.name}
                                        {feature.subtext && (
                                            <div className="subtext">
                                                {feature.subtext}
                                            </div>
                                        )}
                                    </td>
                                    {plans.map((plan, planIndex) => (
                                        <td
                                            key={plan.id}
                                            className={selectedPlanId === plan.id ? 'selected' : ''}
                                        >
                                            {renderValue(feature.values[planIndex])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            <tr className="section-divider"></tr>
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            </div>
            <div className="pricing-cards-mobile">
                {plans.map((plan, planIndex) => (
                    <div
                        key={`card-${plan.id}`}
                        className={`plan-card ${selectedPlanId === plan.id ? 'selected-card' : ''}`}
                    >
                        <h2>{plan.name}</h2>
                        <p className="plan-card-price">
                            {plan.price}
                            {plan.priceDetail && (
                                <span className="price-detail">{plan.priceDetail}</span>
                            )}
                        </p>
                        {pricingData.map((section, sectionIndex) => (
                            <div key={sectionIndex} className="card-section">
                                <h3>{section.category}</h3>
                                {section.features.map((feature, featureIndex) => (
                                    <div key={featureIndex} className="card-feature">
                                        <p>
                                            {feature.name}
                                            {/* Carries "Coming Soon" / "to 190+ DSPs" across to
                                                the cards — the table shows these, the cards
                                                used to drop them silently. */}
                                            {feature.subtext && (
                                                <span className="subtext">{feature.subtext}</span>
                                            )}
                                        </p>
                                        {renderValue(feature.values[planIndex])}
                                    </div>
                                ))}
                            </div>
                        ))}

                        {/* Map button for each card */}
                        <Box className="pricing-buttons">
                            <UnifiedButton
                                className="pricing-buttons-second"
                                textContent={plan.cta}
                                onClick={handlePlanCta(plan)}
                                loader={payingPlanId === plan.id}
                                disabled={payingPlanId === plan.id}
                            />
                        </Box>
                    </div>
                ))}
            </div>



            <Box className="pricing-buttons-table">
                {plans.map((plan) => (
                    <UnifiedButton
                        key={plan.id}
                        className="pricing-buttons-second"
                        textContent={plan.cta}
                        onClick={handlePlanCta(plan)}
                        loader={payingPlanId === plan.id}
                        disabled={payingPlanId === plan.id}
                    />
                ))}
            </Box>
        </div>
    );
};

export default SecondPricingSection;
